import {useCreateOrUpdateXcDisclaimerMutation, useDeleteXcDisclaimerMutation, useXcDisclaimersQuery, XcDisclaimersDocument} from '../generated/graphql'
import generatedGraphql from '../generated/possibleTypes'
import {getGraphQlFragmentVariables} from '../client'

import type {DisclaimerInput, XcDisclaimersQuery, XcDisclaimersQueryVariables} from '../generated/graphql'

const recordTypes = generatedGraphql.possibleTypes['XCDisclaimerInterface']

export const useDisclaimerCrud = (collectionId: string) => {

  const [deleteDisclaimerMutation] = useDeleteXcDisclaimerMutation()
  const [updateDisclaimerMutation] = useCreateOrUpdateXcDisclaimerMutation()

  const {data, loading, error} = useXcDisclaimersQuery({variables: {collectionId}})
  const disclaimers = data?.xcDisclaimers ?? []

  const deleteDisclaimer = async (disclaimerId: string) => {
    return await deleteDisclaimerMutation({
      variables: {disclaimerId}, update: (cache, data) => {
        if (!data) return
        for (const possibleType of recordTypes) {
          cache.evict({id: `${possibleType}:${disclaimerId}`})
        }
      },
    })
  }

  const createOrUpdateDisclaimer = async(disclaimerInput: DisclaimerInput) => {
    return await updateDisclaimerMutation({
      variables: {data: disclaimerInput},
      update: (cache, data) => {
        if (!data.data) return
        if ( // this is an update -> auto cache update
          disclaimerInput.xcInfoFootnote?.disclaimerId ||
          disclaimerInput.xcRectangleFootnote?.disclaimerId ||
          disclaimerInput.xcDoc?.disclaimerId
        ) return

        // cache.modify has no typings on ROOT_QUERY - fallback to old style with update query :/

        const cacheData = cache.readQuery<XcDisclaimersQuery, XcDisclaimersQueryVariables>({
          query: XcDisclaimersDocument,
          variables: {collectionId, ...getGraphQlFragmentVariables()},
        })

        // if this is a update by file, there are likly mixed updates and creates
        // therefore we only need to update the cache with the new data
        const oldData = cacheData?.xcDisclaimers ?? []
        const oldIds = oldData.map(disclaimer => disclaimer.id) ?? []
        const incomingNewData = data.data.createOrUpdateXcDisclaimer.filter(newData => !oldIds.includes(newData.id))

        cache.writeQuery<XcDisclaimersQuery, XcDisclaimersQueryVariables>({
          query: XcDisclaimersDocument,
          variables: {collectionId, ...getGraphQlFragmentVariables()},
          data: {__typename: 'Query', xcDisclaimers: [...oldData, ...incomingNewData]},
        })
      },
    })
  }

  return {data: disclaimers, loading, error, deleteDisclaimer, createOrUpdateDisclaimer}

}